import React, { useEffect, useRef } from 'react';

const extractVideoID = (url) => {
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const matches = url.match(regex);
  return matches ? matches[1] : null;
};

const loadYouTubeIframeAPI = () => {
  return new Promise((resolve, reject) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prevCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prevCallback === 'function') prevCallback();
      resolve(window.YT);
    };
    const existingScriptTag = document.getElementById('youtube-iframe-script');
    if (existingScriptTag) return;
    const scriptTag = document.createElement('script');
    scriptTag.src = 'https://www.youtube.com/iframe_api';
    scriptTag.id = 'youtube-iframe-script';
    document.body.appendChild(scriptTag);
    scriptTag.onerror = (error) => reject(error);
  });
};

const YouTubePlayer = ({ url , is_valid=true, playerId = 'youtube-player' }) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const resizeHandlerRef = useRef(null);
  const videoId = extractVideoID(url);

  useEffect(() => {
    let isMounted = true;

    const clearResize = () => {
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
    };

    const destroyPlayer = () => {
      clearResize();
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (_) {}
        playerRef.current = null;
      }
    };

    if (!url || !is_valid || !videoId) {
      destroyPlayer();
      return () => {
        isMounted = false;
      };
    }

  loadYouTubeIframeAPI().then((YT) => {
    if (!isMounted) return;
    const container = containerRef.current;
    if (!container) return;

    destroyPlayer();

    playerRef.current = new YT.Player(container, {
      videoId,
      events: {
        onReady: (event) => {
          if (!isMounted) return;
          container.style.width = '100%';
          container.style.maxWidth = '100%';
          const iframe = event.target.getIframe();
          const updateSize = () => {
            const parentWidth = container.clientWidth;
            const width = parentWidth || iframe.clientWidth;
            const height = (width * 9) / 16;
            iframe.style.width = '100%';
            iframe.style.maxWidth = '100%';
            iframe.style.height = `${height}px`;
            iframe.style.display = 'block';
          };
          resizeHandlerRef.current = updateSize;
          updateSize();
          window.addEventListener('resize', updateSize);
          event.target.setVolume(50);
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.ENDED) {
            event.target.playVideo();
          }
        },
        onError: (event) => console.error('Error loading YouTube player:', event),
      },
      playerVars: {
        autoplay: 0,
        controls: 1,
        modestbranding: 1,
        loop: 1,
        playlist: videoId,
        fs: 1,
        cc_load_policy: 0,
        iv_load_policy: 3,
        autohide: 0,
        rel: 0,
        disablekb: 1,
      },
    });
  });

    return () => {
      isMounted = false;
      destroyPlayer();
    };
  }, [url, is_valid, videoId]);

  return (
    <div style={{ width: '100%', height: 'auto', minHeight: is_valid ? undefined : 0 }}>
      <div id={playerId} ref={containerRef} style={{ width: '100%', maxWidth: '100%' }} />
    </div>
  );
};

export default YouTubePlayer;
